import AmnisButton from 'components/ui/AmnisButton/AmnisButton';
import CheckBoxGroup from 'components/ui/Form/OnboardingCheckBoxGroup/OnboardingCheckBoxGroup';
import {CrpVariants} from 'constants/businessPartnerAttributes';
import styles from './CrpRoleSelector.module.scss';
import Form, {FormOnSubmit} from 'components/ui/Form/Form/Form';
import {ICrpBpListItem} from 'api/business-partner';
import {User} from 'api/users';
import {useEffect, useMemo} from 'react';
import {CrpSelectedRoles} from 'components/pages/OnboardingPage/components/CrpForm';
import {LegalType} from 'types/onboarding';
import {useForm} from 'react-hook-form';
import useModal from 'components/ui/ModalContainer/useModal';
import CrpInfo from 'components/pages/OnboardingPage/components/Infos/CrpInfo';
import OnboardingOverlay from 'components/ui/Overlays/OnboardingOverlay/OnboardingOverlay';

export interface ICrpRoleSelectorValues {
    roles: CrpSelectedRoles;
}

interface IProps {
    onSubmit: FormOnSubmit<ICrpRoleSelectorValues>;
    crpBpData?: ICrpBpListItem;
    user: User;
    legalType: LegalType | null;
}

const CrpRoleSelector = ({onSubmit, crpBpData, user, legalType}: IProps) => {
    const {closeModal, openModalWithContent} = useModal();
    const defaultValue = useMemo(() => {
        const result: CrpSelectedRoles = [];

        if (
            user.crp === crpBpData?.crp['@id'] ||
            legalType === LegalType.INDIVIDUAL
        ) {
            result.push('currentUser');
        }

        if (crpBpData?.isOwner || legalType === LegalType.INDIVIDUAL) {
            result.push(CrpVariants.OWNER);
        }

        if (crpBpData?.hasSigningRights || legalType === LegalType.INDIVIDUAL) {
            result.push(CrpVariants.SIGNER);
        }

        if (crpBpData?.contractSigner || legalType === LegalType.INDIVIDUAL) {
            result.push(CrpVariants.CONTRACT_SIGNER);
        }

        if (crpBpData?.admin || legalType === LegalType.INDIVIDUAL) {
            result.push(CrpVariants.ADMIN);
        }

        return result;
    }, [crpBpData, user]);

    const formMethods = useForm<ICrpRoleSelectorValues>({
        defaultValues: {
            roles: defaultValue,
        },
    });

    const selected = formMethods.watch('roles');

    useEffect(() => {
        if (
            !selected.includes(CrpVariants.SIGNER) &&
            selected.includes(CrpVariants.CONTRACT_SIGNER)
        ) {
            formMethods.setValue(
                'roles',
                selected.filter(s => s !== CrpVariants.CONTRACT_SIGNER),
            );
        }
    }, [selected]);

    const options = useMemo(() => {
        const options: {
            label: string;
            value: CrpVariants | string;
            disabled?: boolean;
        }[] = [
            {
                label: 'Owner',
                value: CrpVariants.OWNER,
                disabled: legalType === LegalType.INDIVIDUAL,
            },
            {
                label: 'Authorized person',
                value: CrpVariants.SIGNER,
                disabled: legalType === LegalType.INDIVIDUAL,
            },
            {
                label: 'Contract signer',
                value: CrpVariants.CONTRACT_SIGNER,
                disabled:
                    legalType === LegalType.INDIVIDUAL ||
                    !selected.includes(CrpVariants.SIGNER),
            },
            {
                label: 'Admin',
                value: CrpVariants.ADMIN,
                disabled: legalType === LegalType.INDIVIDUAL,
            },
        ];

        if (!user.crp || crpBpData?.crp['@id'] === user.crp) {
            options.unshift({
                label: 'It is me',
                value: 'currentUser',
                disabled: crpBpData?.crp['@id'] === user.crp,
            });
        }

        return options;
    }, [crpBpData, user, selected]);

    return (
        <Form
            onSubmit={onSubmit}
            formMethods={formMethods}
            className={styles.container}
        >
            <CheckBoxGroup
                label={
                    <div className={styles.label}>
                        <span>What roles this person have?</span>
                        <AmnisButton
                            endIcon="arrowOutward"
                            intent="terciary"
                            size="xsmall"
                            onClick={() =>
                                openModalWithContent(
                                    'onboardingStepInfo',
                                    <OnboardingOverlay
                                        modalKey="onboardingStepInfo"
                                        align="right"
                                        fullHeight
                                    >
                                        <CrpInfo />
                                    </OnboardingOverlay>,
                                )
                            }
                        >
                            Learn more
                        </AmnisButton>
                    </div>
                }
                name="roles"
                options={options}
                variant="dark"
            />
            <div className={styles.buttonRow}>
                <AmnisButton type="submit">Continue</AmnisButton>
                <AmnisButton intent="secondary" onClick={() => closeModal()}>
                    Cancel
                </AmnisButton>
            </div>
        </Form>
    );
};

export default CrpRoleSelector;
